import { useCallback, useEffect, useState } from "react";
import { inductionService } from "../services/inductionService";
import { InductionData, InductionFeedback } from "../Types";

export function useInduction() {
  const [data, setData] = useState<InductionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

useEffect(() => {
  let cancelled = false;

  const loadInduction = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await inductionService.getInduction();

      if (cancelled) return;

      if (!result?.sections?.length) {
        throw new Error("No induction sections received from API.");
      }

      setData(result);
      setIndex(result.lastSectionIndex ?? 0);
    } catch (error) {
      if (!cancelled) {
        console.error("Induction loading error:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Unable to load induction."
        );
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  };

  loadInduction();

  return () => {
    cancelled = true;
  };
}, []);

  const total = data?.sections.length ?? 0;
  const lastIndex = Math.max(0, total - 1);

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 0), lastIndex);
      setIndex(clamped);
      // Progress save is fire-and-forget; a failed save must not block the employee
      inductionService.saveProgress(clamped).catch(() => undefined);
    },
    [lastIndex]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  /** Feedback is mandatory: submit it, then mark the induction complete. */
  const finish = useCallback(async (feedback: InductionFeedback) => {
    setSubmitting(true);
    setError(null);
    try {
      await inductionService.submitFeedback(feedback);
      await inductionService.complete();
      return true;
    } catch {
      setError("We could not submit your feedback. Please try again.");
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  return {
    data,
    loading,
    error,
    index,
    total,
    lastIndex,
    section: data?.sections[index],
    percent: total ? Math.round(((index + 1) / total) * 100) : 0,
    submitting,
    goTo,
    next,
    prev,
    finish,
  };
}